import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { find, reduce, without } from 'lodash';
import * as yup from 'yup';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { DefaultFilledButton, PrimaryButton, ResponsiveButtonGroup } from '@magoosh/lib/button';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';

import * as api from '@test_prep/app/api';
import { useAppContext } from '@test_prep/app/context';
import { isIELTSExamName } from '@test_prep/app/pages/analytics/exam_name_utils';

interface Props {
  onClose: () => void;
  onUpdate: () => void;
}

// This component is meant to be presented as the modal body in a setModal call
const TargetScoresModal: React.FC<Props> = (props) => {
  const { branding } = useAppContext().data;
  const [targetScoreConfig, setTargetScoreConfig] = useState(null);

  // IELTS scores in half-band increments (e.g. 6.5); other exams take whole-number targets
  const scoreStep = isIELTSExamName(branding.examName) ? 0.5 : 1;

  // The number input's step is advisory, so reject off-increment values (e.g. 6.7) here
  const withScoreIncrement = (schema) => {
    if (scoreStep !== 0.5) return schema;

    return schema.test(
      'half-band-increment',
      // eslint-disable-next-line no-template-curly-in-string
      '${path} must be a whole or half band (e.g. 6.5)', // yup swaps ${path} for the label
      (value) => value == null || Number.isInteger(value * 2)
    );
  };

  useEffect(() => {
    api.fetchUserProfile().then((userProfile) => {
      setTargetScoreConfig(userProfile.targetScores);
    });
  }, []);

  const handleSubmit = (values) => {
    return api.updateUserProfile({ userProfile: values }).then(() => {
      props.onUpdate();
      props.onClose();
    });
  };

  if (targetScoreConfig) {
    const rootTargetScore = find(targetScoreConfig, (targetScore) => targetScore.examSection.isRoot);
    const sectionTargetScores = without(targetScoreConfig, rootTargetScore);

    const initialData = {
      targetScore: rootTargetScore.target,
      targetSectionScores: reduce(sectionTargetScores, (output, targetScore) => {
        output[targetScore.examSection.id] = targetScore.target;
        return output;
      }, {})
    };

    const validationSchema = yup.object({
      targetScore: withScoreIncrement(
        yup.number().nullable().min(
          rootTargetScore.examSection.scoreRange.low
        ).max(
          rootTargetScore.examSection.scoreRange.high
        ).label('Overall target score')
      ),
      targetSectionScores: yup.object().shape(
        reduce(sectionTargetScores, (output, targetScore) => {
          output[targetScore.examSection.id] = withScoreIncrement(
            yup.number().nullable().min(
              targetScore.examSection.scoreRange.low
            ).max(
              targetScore.examSection.scoreRange.high
            ).label(`${targetScore.examSection.name} target score`)
          );
          return output;
        }, {})
      )
    });

    return (
      <Formik
        initialValues={initialData}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <div>
              <FieldInput
                label={
                  <>
                    Overall{' '}
                    <span className="small text-gray-light text-normal">
                      ({rootTargetScore.examSection.scoreRange.low} - {rootTargetScore.examSection.scoreRange.high})
                    </span>
                  </>
                }
                name="targetScore"
                step={scoreStep}
                type="number"
              />
            </div>

            <div className="d-flex flex-column flex-lg-row flex-wrap" style={{ columnGap: 20 }}>
              {
                sectionTargetScores.map((targetScore) => (
                  <div key={targetScore.examSection.id} style={{ flexBasis: 'calc(50% - 10px)' }}>
                    <FieldInput
                      label={
                        <>
                          {targetScore.examSection.name}{' '}
                          <span className="small text-gray-light text-normal">
                            ({targetScore.examSection.scoreRange.low} - {targetScore.examSection.scoreRange.high})
                          </span>
                        </>
                      }
                      name={`targetSectionScores.${targetScore.examSection.id}`}
                      step={scoreStep}
                      type="number"
                    />
                  </div>
                ))
              }
            </div>

            <div className="u-margin-T-s">
              <ResponsiveButtonGroup>
                <PrimaryButton submitting={isSubmitting} type="submit">
                  Save Target Scores
                </PrimaryButton>

                <DefaultFilledButton onClick={props.onClose}>
                  Cancel
                </DefaultFilledButton>
              </ResponsiveButtonGroup>
            </div>
          </Form>
        )}
      </Formik>
    );
  } else {
    return (
      <div className="d-flex justify-content-center">
        <LoadingIndicator />
      </div>
    );
  }
};

export default TargetScoresModal;
